import { create, useModal } from '@ebay/nice-modal-react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Roles } from 'assets/icons/Roles.svg';
import NoResultsPlaceholder from 'components/NoResultsPlaceholder/NoResultsPlaceholder';
import useScopedUserPowerLevels from 'hooks/useScopedUserPowerLevels/useScopedUserPowerLevels';
import { DefaultRoleTypesEnum } from 'modules/Workspaces/hooks/useCreateWorkspaceRole/utils';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';

import UneditableRole from './UneditableRole';
import WorkspaceRoleEditForm from './WorkspaceRoleEditForm';

interface WorkspaceRoleEditModalProps {
  roleId: string;
}

const WorkspaceRoleEditModal = create<WorkspaceRoleEditModalProps>(({ roleId }) => {
  const { t } = useTranslation();
  const { remove } = useModal(WorkspaceRoleEditModal);
  const { workspaceId, workspaceRoles, workspacePermissions } = useWorkspacesContext({ requireWorkspaceId: true });
  const currentUserWorkspacePowerLevel = useScopedUserPowerLevels(workspaceId).workspace;

  const role = workspaceRoles.find((candidate) => candidate.id === roleId);
  if (!role) {
    return <NoResultsPlaceholder className="bg-white" title={t('workspaces.no_role_assigned')} icon={Roles} />;
  }

  // AC-075..AC-078 + tech.md §5.2 editability matrix. Read-only when any of:
  //  - actor lacks adjustRoles
  //  - role is a default (OWNER or MEMBER — always 0 PL, not editable)
  //  - role is inherited from a parent workspace
  //  - role's workspace PL is at or above the actor's workspace PL
  const isDefaultRole =
    role.defaultRoleType === DefaultRoleTypesEnum.OWNER || role.defaultRoleType === DefaultRoleTypesEnum.MEMBER;
  const isInherited = role.inherited === true;
  const hasAdjustRoles = workspacePermissions.adjustRoles;
  const isAboveActor = role.powerLevels.workspace >= currentUserWorkspacePowerLevel;
  const isReadOnly = !hasAdjustRoles || isDefaultRole || isInherited || isAboveActor;

  if (isReadOnly) {
    return <UneditableRole role={role} onClose={remove} />;
  }

  return <WorkspaceRoleEditForm role={role} workspaceId={workspaceId} onClose={remove} />;
});

export default WorkspaceRoleEditModal;
